import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  publicContactSchema,
  PUBLIC_CONTACT_TOPICS,
} from "@fysaro/schemas";
import { FRONTEND_ROUTES } from "@fysaro/shared";
import { z } from "zod";
import { Input } from "@/design-system/components/ui/input";
import { Label } from "@/design-system/components/ui/label";
import { Textarea } from "@/design-system/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/design-system/components/ui/select";
import { Checkbox } from "@/design-system/components/ui/checkbox";
import { LoadingButton } from "@/shared/ui/components/LoadingButton";
import { FORM_STYLES } from "@/design-system/lib/form-styles";
import { cn } from "@/design-system/utils";
import { api } from "@/shared/api/axios";
import { CheckCircle2 } from "lucide-react";
import { SURFACE_STYLES } from "@/design-system/lib/surface-styles";

type PublicContactFormValues = z.infer<typeof publicContactSchema>;

export function PublicContactForm() {
  const { t } = useTranslation(["landing"]);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<PublicContactFormValues>({
    resolver: zodResolver(publicContactSchema),
    defaultValues: {
      name: "",
      email: "",
      topic: "general",
      message: "",
      acceptPrivacy: undefined,
    },
  });

  const errors = form.formState.errors;
  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = async (values: PublicContactFormValues) => {
    setSubmitError(null);
    try {
      await api.post("/public/contact", values);
      setSubmitted(true);
    } catch {
      setSubmitError(t("landing:contact_form.error"));
    }
  };

  if (submitted) {
    return (
      <div className={cn(SURFACE_STYLES.panel, "rounded-2xl p-8 text-center space-y-3")}>
        <CheckCircle2 className="w-10 h-10 text-primary mx-auto" />
        <h3 className="text-xl font-bold">{t("landing:contact_form.success_title")}</h3>
        <p className="text-sm text-muted-foreground">
          {t("landing:contact_form.success_body")}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className={cn(SURFACE_STYLES.panel, "rounded-2xl p-8 space-y-5")}
    >
      <div>
        <h3 className="text-xl font-bold">{t("landing:contact_form.title")}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t("landing:contact_form.subtitle")}
        </p>
      </div>

      <div className={FORM_STYLES.fieldWrapper}>
        <Label className={FORM_STYLES.label}>{t("landing:contact_form.name_label")}</Label>
        <Input
          {...form.register("name")}
          placeholder={t("landing:contact_form.name_placeholder")}
          className={cn(FORM_STYLES.input, errors.name && FORM_STYLES.inputError)}
        />
        {errors.name && (
          <p className={FORM_STYLES.errorText}>{errors.name.message}</p>
        )}
      </div>

      <div className={FORM_STYLES.fieldWrapper}>
        <Label className={FORM_STYLES.label}>{t("landing:contact_form.email_label")}</Label>
        <Input
          type="email"
          {...form.register("email")}
          placeholder={t("landing:contact_form.email_placeholder")}
          className={cn(FORM_STYLES.input, errors.email && FORM_STYLES.inputError)}
        />
        {errors.email && (
          <p className={FORM_STYLES.errorText}>{errors.email.message}</p>
        )}
      </div>

      <div className={FORM_STYLES.fieldWrapper}>
        <Label className={FORM_STYLES.label}>{t("landing:contact_form.topic_label")}</Label>
        <Select
          value={form.watch("topic")}
          onValueChange={(value) =>
            form.setValue("topic", value as PublicContactFormValues["topic"], {
              shouldValidate: true,
            })
          }
        >
          <SelectTrigger className={FORM_STYLES.input}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PUBLIC_CONTACT_TOPICS.map((topic) => (
              <SelectItem key={topic} value={topic}>
                {t(`landing:contact_form.topics.${topic}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.topic && (
          <p className={FORM_STYLES.errorText}>{errors.topic.message}</p>
        )}
      </div>

      <div className={FORM_STYLES.fieldWrapper}>
        <Label className={FORM_STYLES.label}>{t("landing:contact_form.message_label")}</Label>
        <Textarea
          {...form.register("message")}
          rows={5}
          placeholder={t("landing:contact_form.message_placeholder")}
          className={cn(errors.message && FORM_STYLES.inputError)}
        />
        {errors.message && (
          <p className={FORM_STYLES.errorText}>{errors.message.message}</p>
        )}
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id="acceptPrivacy"
          checked={form.watch("acceptPrivacy") === true}
          onCheckedChange={(checked) =>
            form.setValue("acceptPrivacy", checked === true ? true : (undefined as never), {
              shouldValidate: true,
            })
          }
        />
        <Label htmlFor="acceptPrivacy" className="text-sm leading-snug font-normal">
          {t("landing:contact_form.privacy_prefix")}{" "}
          <Link
            to={FRONTEND_ROUTES.public.legalPrivacy}
            className="text-primary hover:underline"
          >
            {t("landing:contact_form.privacy_link")}
          </Link>
        </Label>
      </div>
      {errors.acceptPrivacy && (
        <p className={FORM_STYLES.errorText}>{errors.acceptPrivacy.message}</p>
      )}

      {submitError && (
        <p className="text-destructive text-sm">{submitError}</p>
      )}

      <LoadingButton
        type="submit"
        className={FORM_STYLES.submitButton}
        isLoading={isSubmitting}
        loadingLabel={t("landing:contact_form.submitting")}
      >
        {t("landing:contact_form.submit")}
      </LoadingButton>
    </form>
  );
}
